import React, { useEffect, useMemo, useState } from "react";
import {
  FaUserMinus,
  FaCalendarAlt,
  FaUsers,
  FaSearch,
  FaMapSigns,
  FaTrain,
  FaBed,
} from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import {
  getAllActorsByEventId,
  deleteActorFromEvent,
} from "./../../../services/acteureventServices";
import formatDate from "./../../../utils/formatDate";

const normalize = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const getActorPivot = (actor) =>
  actor?.ActeurEvent || actor?.ActeursEvent || actor?.ActeursEvents || {};

const getResponseLabel = (status) => {
  switch (status) {
    case "accepted":
      return "Acceptée";
    case "rejected":
      return "Refusée";
    default:
      return "En attente";
  }
};

const ActorUnregistrationModal = ({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  eventStart,
  refreshList,
}) => {
  const [actors, setActors] = useState([]);
  const [filter, setFilter] = useState("");
  const [loadingActors, setLoadingActors] = useState(false);
  const [submittingActorId, setSubmittingActorId] = useState(null);

  const titleId = "actor-unregistration-modal-title";
  const descriptionId = "actor-unregistration-modal-description";

  useEffect(() => {
    if (isOpen && eventId) {
      fetchActors();
      setFilter("");
    }
  }, [isOpen, eventId]);

  const fetchActors = async () => {
    try {
      setLoadingActors(true);

      const data = await getAllActorsByEventId(eventId);
      const sortedActors = (data?.Acteur || []).sort((a, b) => {
        const nameA = `${a.nom} ${a.prenom}`.toLowerCase();
        const nameB = `${b.nom} ${b.prenom}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setActors(sortedActors);
    } catch (error) {
      console.error("Erreur lors du chargement des acteurs", error);
      toast.error("Erreur lors du chargement des acteurs.");
      setActors([]);
    } finally {
      setLoadingActors(false);
    }
  };

  const filteredActors = useMemo(() => {
    if (!filter) return actors;

    const search = normalize(filter);

    return actors.filter((actor) => {
      const pivot = getActorPivot(actor);
      const nomPrenom = normalize(`${actor.nom} ${actor.prenom}`);
      const poste = normalize(pivot.poste);
      const club = normalize(actor.club_acteur);
      const tapis = normalize(pivot.tapis);
      return (
        nomPrenom.includes(search) ||
        poste.includes(search) ||
        club.includes(search) ||
        tapis.includes(search)
      );
    });
  }, [actors, filter]);

  const handleUnregister = async (acteurId, label) => {
    if (
      !window.confirm(
        `Confirmez-vous la désinscription de ${label} sur cet événement ?`,
      )
    ) {
      return;
    }

    try {
      setSubmittingActorId(acteurId);

      await deleteActorFromEvent(acteurId, eventId);
      toast.success("Acteur désinscrit avec succès.");

      await fetchActors();
      await refreshList?.();
    } catch (error) {
      console.error("Erreur lors de la désinscription de l'acteur", error);
      toast.error("Erreur lors de la désinscription de l'acteur.");
    } finally {
      setSubmittingActorId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById={titleId}
      describedById={descriptionId}
      initialFocusSelector='input[name="actor-unregister-filter"]'
    >
      <ModalHeader
        id={titleId}
        title="Désinscrire un acteur"
        subtitle="Retrait d’une convocation sur l’événement"
        onClose={onClose}
      />

      <ModalBody id={descriptionId}>
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                <FaCalendarAlt />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Événement
                </p>

                <div className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                  {eventTitle || "Événement non trouvé"}
                </div>

                {eventStart && (
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Date :{" "}
                    <span className="font-medium">
                      {formatDate(eventStart)}
                    </span>
                  </div>
                )}

                <p className="mt-2 text-xs italic text-slate-500 dark:text-slate-400">
                  Cliquez sur “Désinscrire” pour retirer un acteur de la
                  convocation.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              Filtrer les acteurs convoqués
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-autofocus
                name="actor-unregister-filter"
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Nom, prénom, poste, club, tapis…"
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 dark:text-slate-300">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
              <FaUsers />
              {loadingActors
                ? "Chargement des acteurs…"
                : `${filteredActors.length} acteur(s) affiché(s)`}
            </span>

            {!loadingActors &&
              actors.length > 0 &&
              filteredActors.length !== actors.length && (
                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  {actors.length} convoqué(s) au total
                </span>
              )}
          </div>

          <div className="space-y-3">
            {loadingActors ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                Chargement des acteurs convoqués…
              </div>
            ) : filteredActors.length > 0 ? (
              filteredActors.map((actor) => {
                const pivot = getActorPivot(actor);
                const acteurId = pivot.acteur_id || actor.id;
                const poste = pivot.poste || "Non renseigné";
                const label = `${actor.nom?.toUpperCase()} ${actor.prenom}${
                  pivot.poste ? ` - ${pivot.poste}` : ""
                }`;

                return (
                  <div
                    key={`${acteurId}-${eventId}`}
                    className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between dark:border-slate-700 dark:bg-slate-950"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 dark:text-white">
                        {actor.nom?.toUpperCase()} {actor.prenom}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                          Poste :{" "}
                          <span className="ml-1 font-medium">{poste}</span>
                        </span>

                        {pivot.tapis && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                            <FaMapSigns />
                            {pivot.tapis}
                          </span>
                        )}

                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                          Réponse :{" "}
                          <span className="ml-1 font-medium">
                            {getResponseLabel(pivot.response_status)}
                          </span>
                        </span>

                        {actor.club_acteur && (
                          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                            Club :{" "}
                            <span className="ml-1 font-medium">
                              {actor.club_acteur}
                            </span>
                          </span>
                        )}

                        {pivot.need_transport_support === true && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                            <FaTrain />
                            Transport
                          </span>
                        )}

                        {pivot.need_accommodation_support === true && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                            <FaBed />
                            Hébergement
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="sm:shrink-0">
                      <Button
                        onClick={() => handleUnregister(acteurId, label)}
                        className="!rounded-2xl !bg-red-600 hover:!bg-red-700"
                        text={
                          submittingActorId === acteurId
                            ? "Désinscription..."
                            : "Désinscrire"
                        }
                        icon={FaUserMinus}
                        disabled={submittingActorId === acteurId}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                Aucun acteur n’est actuellement inscrit sur cet événement.
              </div>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          onClick={onClose}
          className="!rounded-2xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
          text="Fermer"
        />
      </ModalFooter>
    </Modal>
  );
};

export default ActorUnregistrationModal;
